<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, reactive } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PendentesIndex from '../pendingMarketUpdates/pages/PendentesIndex.vue';
import MudancasIndex from '../mudancas/pages/MudancasIndex.vue';

const route = useRoute();
const { t } = useI18n();
const mercadoLabel = computed(() => t('MERCADO.ARIA_LABEL'));
const routeQueryValue = key => {
  const value = route.query[key];
  return Array.isArray(value) ? value[0] : value;
};

const sharedQueryKeys = [
  'updatedSince',
  'updatedUntil',
  'desde',
  'ate',
  'sourceKind',
  'usuario_email',
  'user',
];

const tabStates = reactive({});

const liveTabs = computed(() =>
  [
    {
      key: 'pendentes',
      label: t('MERCADO.TABS.PENDENTES.LABEL'),
      helper: t('MERCADO.TABS.PENDENTES.HELPER'),
      icon: 'i-lucide-clipboard-list',
      routeName: 'pending_market_updates_index',
      query: {},
    },
    {
      key: 'mudancas',
      label: t('MERCADO.TABS.MUDANCAS.LABEL'),
      helper: t('MERCADO.TABS.MUDANCAS.HELPER'),
      icon: 'i-lucide-git-compare-arrows',
      routeName: 'mudancas_index',
      query: {},
    },
    {
      key: 'laudos',
      label: t('MERCADO.TABS.LAUDOS.LABEL'),
      helper: t('MERCADO.TABS.LAUDOS.HELPER'),
      icon: 'i-lucide-file-check-2',
      routeName: 'pending_market_updates_index',
      query: { surface: 'operation' },
    },
    {
      key: 'auditoria',
      label: t('MERCADO.TABS.AUDITORIA.LABEL'),
      helper: t('MERCADO.TABS.AUDITORIA.HELPER'),
      icon: 'i-lucide-shield-check',
      routeName: 'mudancas_index',
      query: { auditStatus: 'unverified' },
    },
  ].map(tab => ({
    ...tab,
    state: tabStates[tab.key] || null,
  }))
);

const isMudancasRoute = computed(() => route.name === 'mudancas_index');
const activeComponent = computed(() =>
  isMudancasRoute.value ? MudancasIndex : PendentesIndex
);

const activeTabKey = computed(() => {
  if (route.name === 'pending_market_updates_index') {
    return routeQueryValue('surface') === 'operation' ? 'laudos' : 'pendentes';
  }

  if (route.name === 'mudancas_index') {
    return routeQueryValue('auditStatus') === 'unverified'
      ? 'auditoria'
      : 'mudancas';
  }

  return 'pendentes';
});

const sharedQuery = computed(() =>
  sharedQueryKeys.reduce((query, key) => {
    if (route.query[key]) query[key] = route.query[key];
    return query;
  }, {})
);

const tabRoute = tab => ({
  name: tab.routeName,
  params: route.params,
  query: {
    ...sharedQuery.value,
    ...tab.query,
  },
});

const isTabStateLoading = state => state?.status === 'loading';
const isTabStateUnavailable = state =>
  ['error', 'unavailable'].includes(state?.status);
const tabStateLabel = state => {
  if (isTabStateLoading(state)) return t('MERCADO.STATUS.LOADING');
  if (isTabStateUnavailable(state)) {
    return state?.message || t('MERCADO.STATUS.UNAVAILABLE');
  }
  return '';
};
const handleMercadoTabState = state => {
  if (!state?.key) return;
  tabStates[state.key] = {
    status: state.status || 'ready',
    message: state.message || '',
  };
};
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-[#f7f8fb] font-redHatText">
    <nav
      class="shrink-0 border-b border-lais-border bg-white px-4 py-3 sm:px-6 lg:px-10"
      :aria-label="mercadoLabel"
    >
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:flex">
        <RouterLink
          v-for="tab in liveTabs"
          :key="tab.key"
          :to="tabRoute(tab)"
          class="group flex min-w-0 items-center gap-3 rounded-lais border px-3 py-2 text-left transition-colors xl:min-w-[190px]"
          :class="
            activeTabKey === tab.key
              ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
              : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
          "
          :aria-current="activeTabKey === tab.key ? 'page' : undefined"
        >
          <span
            class="grid size-9 shrink-0 place-items-center rounded-full bg-white shadow-lais-soft"
          >
            <span class="size-4" :class="tab.icon" aria-hidden="true" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-[13px] font-semibold">
              {{ tab.label }}
            </span>
            <span class="block truncate text-[11px] opacity-75">
              {{ tab.helper }}
            </span>
          </span>
          <span
            v-if="isTabStateLoading(tab.state)"
            class="i-lucide-loader-circle size-3.5 shrink-0 animate-spin"
            :aria-label="tabStateLabel(tab.state)"
            :title="tabStateLabel(tab.state)"
            data-test="mercado-tab-loading-indicator"
          />
          <span
            v-else-if="isTabStateUnavailable(tab.state)"
            class="i-lucide-cloud-off size-3.5 shrink-0 text-red-600"
            :aria-label="tabStateLabel(tab.state)"
            :title="tabStateLabel(tab.state)"
            data-test="mercado-tab-unavailable-indicator"
          />
        </RouterLink>
      </div>
    </nav>
    <div class="min-h-0 flex-1">
      <component
        :is="activeComponent"
        @mercado-tab-state="handleMercadoTabState"
      />
    </div>
  </div>
</template>
